'use client';

import React from 'react';

const phases = [
  {
    n: '01',
    window: 'Month 1',
    name: 'Foundation',
    color: '#5A5A6E',
    goal: 'Get the non-AI core working so AI has data to work with.',
    items: [
      'Multi-tenant data model (Properties → Units → Leases → Tenants)',
      'Rent collection via Stripe/Plaid',
      'Basic work order system',
      'Tenant portal (web + mobile)',
      'Document storage',
      'Auth & permissions',
    ],
    milestone: 'Core data graph live.',
  },
  {
    n: '02',
    window: 'Months 2–3',
    name: 'Generative AI Layer',
    color: '#A78BFA',
    goal: 'Save users time on writing and reading.',
    items: [
      'Ask Propmatic — natural-language BI chat',
      'Auto-draft tenant communications',
      'AI lease abstraction',
      'Invoice OCR & auto-coding',
      'Smart search everywhere',
      'Auto-generated listing descriptions',
    ],
    milestone: 'Users save 5–10 hours / week.',
  },
  {
    n: '03',
    window: 'Months 4–5',
    name: 'Predictive Layer',
    color: '#22C55E',
    goal: 'Anticipate problems before they happen.',
    items: [
      'Tenant churn risk model',
      'Dynamic rent pricing',
      'Predictive maintenance',
      'Financial forecasting',
      'Anomaly alerts',
      'Vacancy forecasting',
    ],
    milestone: '5–15% NOI improvement for pilot customers.',
  },
  {
    n: '04',
    window: 'Months 6-7',
    name: 'Agentic Layer',
    color: '#7C3AED',
    goal: 'Autonomous workflows that deliver outcomes.',
    items: [
      'Leasing Performer (24/7 leasing agent)',
      'Maintenance Performer (triage + dispatch)',
      'Collections Agent',
      'Month-End Close Agent',
      'Renewal Strategy Agent',
    ],
    milestone: '50%+ of routine PM tasks complete without human involvement.',
  },
  {
    n: '05',
    window: 'Months 8–9',
    name: 'Platform Layer',
    color: '#F59E0B',
    goal: 'Become an ecosystem, not just a product.',
    items: [
      'Agent Marketplace (third-party publishers)',
      'Composer (no-code agent builder)',
      'MCP Connectors (bring your own LLM)',
      'Digital twin integration',
      'Industry-specific agent packs',
    ],
    milestone: 'Network effect — customers choose Propmatic because of its agent ecosystem.',
  },
];

export default function AIRoadmap() {
  return (
    <section id="roadmap" className="py-24 px-6 relative">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 50% 30%, rgba(124,58,237,0.1) 0%, transparent 70%)',
        }}
      />

      <div className="relative max-w-5xl mx-auto">
        <div className="reveal-blur mb-14 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[rgba(124,58,237,0.25)] mb-5">
            <span className="w-1 h-1 rounded-full bg-[#A78BFA] status-pulse" />
            <span className="text-[10px] font-mono text-[#A78BFA] tracking-widest uppercase">
              Development Roadmap · 9 Months
            </span>
          </div>
          <h2
            className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0]"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}
          >
            Five phases.
            <br />
            <span
              style={{
                background: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              One compounding product.
            </span>
          </h2>
        </div>

        {/* Vertical timeline */}
        <div className="relative">
          {/* Spine */}
          <div
            className="absolute left-5 top-2 bottom-2 w-px"
            style={{
              background:
                'linear-gradient(to bottom, rgba(90,90,110,0.3), rgba(124,58,237,0.6), rgba(90,90,110,0.3))',
            }}
          />

          <div className="flex flex-col gap-6 stagger-children">
            {phases.map((p) => (
              <div key={p.n} className="reveal-blur relative pl-16">
                {/* Spine dot */}
                <div
                  className="absolute left-3 top-6 w-5 h-5 rounded-full border-2 flex items-center justify-center"
                  style={{
                    background: '#09090B',
                    borderColor: p.color,
                    boxShadow: `0 0 12px ${p.color}88`,
                  }}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.color }} />
                </div>

                <div
                  className="bento-card card-shine p-6 md:p-7"
                  style={{ borderColor: `${p.color}44` }}
                >
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                    <span
                      className="font-manrope font-extrabold text-2xl tracking-tight"
                      style={{ color: p.color }}
                    >
                      Phase {p.n}
                    </span>
                    <span className="text-[10px] font-mono text-[#5A5A6E] tracking-widest uppercase">
                      {p.window}
                    </span>
                    <span className="font-manrope font-bold text-lg text-[#EEEEF0] tracking-tight">
                      — {p.name}
                    </span>
                  </div>
                  <p className="text-sm text-[#9999A8] leading-relaxed mb-4">{p.goal}</p>
                  <ul className="grid md:grid-cols-2 gap-x-5 gap-y-1.5 mb-4">
                    {p.items.map((it) => (
                      <li key={it} className="flex items-start gap-2 text-xs text-[#EEEEF0]">
                        <span
                          className="mt-1 w-1 h-1 rounded-full shrink-0"
                          style={{ background: p.color }}
                        />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  <div
                    className="text-[11px] font-mono font-semibold border-t border-[rgba(255,255,255,0.05)] pt-3"
                    style={{ color: p.color }}
                  >
                    Milestone · {p.milestone}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
